import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QnAStateType } from '@uber/react-qna-lib';
import type { QuestionnaireT } from '../../common/types/types';
import { BASE_API_URL } from '../consts/base-url';
import type { AnnotationLabelWithMetadataT } from '../types/annotationLabelWithMetadata';
import type { UserGroup } from '../types/userGroup';
import customFetch from '../utils/fetch';
export const useCreateProject = (name: string | null, majorStates: string[], majorStateAfterMetaReviewReject: string, configuration: {
  annotationLabel: AnnotationLabelWithMetadataT[];
  questionnaires: {
    [questionnaireId: string]: {
      questionnaire: QnAStateType;
      name: string;
    };
  };
}, userGroupId: number | null, taskLevelQuestionnaireList: QuestionnaireT[]) => {
  const queryClient = useQueryClient();
  const {
    mutate,
    data,
    isPending,
    isSuccess,
    isError,
    error
  } = useMutation({
    mutationKey: ['createProject', name, majorStates, majorStateAfterMetaReviewReject, configuration, userGroupId, taskLevelQuestionnaireList],
    mutationFn: async ({
      name,
      majorStates,
      majorStateAfterMetaReviewReject,
      configuration,
      userGroupId,
      taskLevelQuestionnaireList
    }: {
      name: string | null;
      majorStates: string[];
      majorStateAfterMetaReviewReject: string;
      configuration: {
        annotationLabel: AnnotationLabelWithMetadataT[];
        questionnaires: {
          [questionnaireId: string]: {
            questionnaire: QnAStateType;
            name: string;
          };
        };
      };
      userGroupId: number | null;
      taskLevelQuestionnaireList: QuestionnaireT[];
    }) => {
      if (name === null) {
        throw new Error('Project name is required');
      }
      if (majorStates.length === 0) {
        throw new Error('At least one major state is required');
      }
      if (majorStateAfterMetaReviewReject.trim() === '') {
        throw new Error('Next state on meta review reject is required');
      }
      const response = await customFetch(`${BASE_API_URL}/projects`, {
        method: 'POST',
        body: JSON.stringify({
          name,
          majorStates,
          nextStateOnMetaReviewReject: majorStateAfterMetaReviewReject,
          configuration: JSON.stringify(configuration),
          userGroupId,
          taskLevelQuestionnaire: taskLevelQuestionnaireList
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const {
        success,
        error,
        data
      } = (await response.json() as {
        success: boolean;
        error: string;
        data: UserGroup;
      });
      if (!response.ok || !success) {
        throw new Error(`Failed on user request: ${error}`);
      }
      queryClient.invalidateQueries({
        queryKey: ['getProjects']
      });
      return {
        data
      };
    }
  });
  return {
    createProject: mutate,
    actionStatus: data,
    isPending,
    isSuccess,
    isError,
    error
  };
};